// ZoneActions — the zone's ONE registry and its overlays (ADR-036 §7 — the four
// kinds; mirrors the exemplar `pages/teams/TeamActions.tsx`).
//
//   • `zoneActs` is the PURE registry — rows with a kind, a section, a gate and a
//     `show` predicate over the status and the closed-over facts; the shape test
//     (`components/patterns/actionGrammar.test.ts`) walks it.
//   • `useZoneActions` resolves it for a zone and returns the ACTS the peek
//     composes from and the OVERLAYS (every dialog and drawer, mounted ONCE by the
//     caller beside the peek).
//
// A zone is PAGELESS (ADR-051 — the peek is its only surface): the primary (Edit
// zone — the one edit, a plain button) rides the peek's top row; the sectioned
// edit lives on its card — the Responsibilities card's link, the verb alone
// ("Manage" — the card's title names what; ADR-036 §7, exception 1) — and never
// in the Edit menu (`ContextPanel pageless`). Its gate is the namer atom
// (`namer:zone` — the people key with the zone level); a namer OUTSIDE this
// zone's own scope (the server's `can_manage_responsibilities`) keeps the link
// DISABLED with "Outside your scope" under it — said, never hidden (ADR-052 §5).
// The door: Set as default (an active zone that is not the default — the one
// default is a disabled zone's never) · Disable / Reactivate · Delete (the danger
// act; the server refuses a used zone, and the reference-delete dialog names
// what still uses it).

import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { ZoneDetail } from '@ems-app/schemas/zones/responses';
import { resolveActs, type ActionItem, type EntityAct } from '../../../components/patterns/entityRegistry.js';
import { ChainsDrawer } from '../../../components/patterns/ChainsDrawer.js';
import { useReferenceDelete } from '../../../components/patterns/ReferenceDelete.js';
import { ConfirmDialog } from '../../../components/ui/ConfirmDialog.js';
import { icons } from '../../../components/ui/icons.js';
import { useCan } from '../../../hooks/useCan.js';
import { useEntitlements } from '../../../hooks/useEntitlements.js';
import { useDeleteZone, useSetZoneDefault, useSetZoneDisabled } from '../../../hooks/useZoneMutations.js';
import { ZoneFormDrawer } from './ZoneFormDrawer.js';
import { useQuickSave } from '../../../components/patterns/forms/useQuickSave.js';

export type ZoneStatus = 'active' | 'disabled';

/** What each act does — the hook wires these to its overlays. */
export interface ZoneActHandlers {
  edit: () => void;
  manageResponsibilities: () => void;
  setDefault: () => void;
  disable: () => void;
  reactivate: () => void;
  remove: () => void;
}

/** The closed-over facts the rows read beside the status (the server's words). */
export interface ZoneActFacts {
  isDefault: boolean;
  /** The viewer may name chains at THIS zone — its own scope covers it (the detail read's word). */
  canManageHere: boolean;
}

/** The zone's registry — pure: the rows, their kinds, sections and predicates. */
export function zoneActs(h: ZoneActHandlers, facts: ZoneActFacts): ActionItem<ZoneStatus>[] {
  return [
    // The primary — the one edit (the record drawer): the peek's main button.
    { id: 'edit', kind: 'primary', labelKey: 'detail.edit', icon: icons.edit, run: { handler: h.edit }, gate: 'reference_data:manage', order: 10 },
    // The Responsibilities card's own act — the verb alone, its card names what (pageless: never the Edit menu).
    {
      id: 'manage-responsibilities',
      kind: 'edit',
      section: 'responsibilities',
      labelKey: 'common:entity.manage',
      icon: icons.responsibilities,
      run: { handler: h.manageResponsibilities },
      gate: 'namer:zone',
      entitlement: 'has_field_force',
      order: 10,
      disabled: !facts.canManageHere,
      disabledNoteKey: 'common:scope.outside',
    },
    // The door — one operation, then the lifecycle; Delete the danger act, fenced last.
    { id: 'set-default', kind: 'operation', labelKey: 'actions.setDefault', icon: icons.check, run: { handler: h.setDefault }, gate: 'reference_data:manage', order: 10, show: (ctx) => ctx.status === 'active' && !facts.isDefault },
    { id: 'disable', kind: 'lifecycle', labelKey: 'actions.disable', icon: icons.statusBlocked, run: { handler: h.disable }, gate: 'reference_data:manage', order: 10, show: (ctx) => ctx.status === 'active' },
    { id: 'reactivate', kind: 'lifecycle', labelKey: 'actions.reactivate', icon: icons.restore, run: { handler: h.reactivate }, gate: 'reference_data:manage', order: 10, show: (ctx) => ctx.status === 'disabled' },
    { id: 'delete', kind: 'lifecycle', labelKey: 'common:reference.delete', icon: icons.delete, run: { handler: h.remove }, gate: 'reference_data:manage', order: 20, tone: 'danger' },
  ];
}

export interface UseZoneActionsOptions {
  /** After a delete (the peek closes). */
  onGone?: () => void;
}

export interface ZoneActions {
  /** The resolved set — what the peek composes from (`ContextPanel acts` · the card's `SectionAct`). */
  acts: EntityAct[];
  /** Every dialog and drawer, mounted ONCE by the caller beside the peek. */
  overlays: ReactNode;
}

export function useZoneActions(projectId: string, zone: ZoneDetail, { onGone }: UseZoneActionsOptions = {}): ZoneActions {
  const { t } = useTranslation('zones');
  const can = useCan(projectId);
  const entitlements = useEntitlements(projectId);
  const setDefault = useSetZoneDefault(projectId);
  const setDisabled = useSetZoneDisabled(projectId);
  // Set as default and Reactivate are low-stakes and direct (no dialog) — quick saves: a refusal said once in its
  // own words, the zone re-read.
  const quickDefault = useQuickSave(setDefault, { key: 'zone-default' });
  const quickSwitch = useQuickSave(setDisabled, { key: 'zone-reactivate' });
  const remove = useDeleteZone(projectId);
  const deletion = useReferenceDelete(async (id) => {
    await remove.mutateAsync(id);
    onGone?.();
  });
  const [editOpen, setEditOpen] = useState(false);
  const [chainsOpen, setChainsOpen] = useState(false);
  const [disableOpen, setDisableOpen] = useState(false);

  const canManageHere = zone.can_manage_responsibilities;

  const acts = resolveActs(
    zoneActs(
      {
        edit: () => setEditOpen(true),
        manageResponsibilities: () => setChainsOpen(true),
        setDefault: () => void quickDefault.save(zone.id),
        disable: () => setDisableOpen(true),
        reactivate: () => void quickSwitch.save({ zoneId: zone.id, disabled: false }),
        remove: () => deletion.ask(zone),
      },
      { isDefault: zone.is_default, canManageHere },
    ),
    { can, entitlements, status: zone.is_disabled ? 'disabled' : 'active' },
    (key) => t(key),
  );

  const overlays = (
    <>
      {/* The record's drawer stays mounted (it slides) — keyed by the zone so a peek→peek switch never keeps the previous seed. */}
      {can('reference_data:manage') && <ZoneFormDrawer key={`edit:${zone.id}`} projectId={projectId} zone={zone} open={editOpen} onClose={() => setEditOpen(false)} />}
      {entitlements.has('has_field_force') && canManageHere && (
        <ChainsDrawer key={`chains:${zone.id}`} projectId={projectId} scope={{ level: 'zone', id: zone.id, name: zone.name, closed: zone.is_disabled }} open={chainsOpen} onClose={() => setChainsOpen(false)} />
      )}
      {/* Disable — a pause said in its own dialog (failure keeps it open with the inline alert). */}
      <ConfirmDialog
        open={disableOpen}
        onClose={() => setDisableOpen(false)}
        tone="danger"
        title={t('actions.disableTitle', { name: zone.name })}
        description={t('actions.disableBody')}
        confirmLabel={t('actions.disable')}
        onConfirm={async () => {
          await setDisabled.mutateAsync({ zoneId: zone.id, disabled: true });
        }}
      />
      {deletion.dialog}
    </>
  );

  return { acts, overlays };
}
